import {Component, ElementRef, EventEmitter, Input, Output, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {TextAutocompleteConfig, TextAutocompleteGroup} from '../text-autocomplete.types';

export interface TextAutocompletePanelPosition {
    top: number;
    left: number;
    width: number;
}

@Component({
    selector: 'mydocma-text-autocomplete-panel',
    templateUrl: './text-autocomplete-panel.component.html',
    styleUrls: ['./text-autocomplete-panel.component.scss']
})
export class TextAutocompletePanelComponent {
    @ViewChild('panel', {static: false}) panelEl?: ElementRef<HTMLElement>;
    @ViewChildren('autocompleteItem', {read: ElementRef}) autocompleteItemButtons: QueryList<ElementRef<HTMLButtonElement>>;

    @Input() filteredGroups: TextAutocompleteGroup[] = [];
    @Input() focusedIndex: number = -1;
    @Input() searchText: string = '';
    @Input() panelClass: string;
    @Input() dropdownCriticalWidth?: number;

    @Output() opened = new EventEmitter<void>();
    @Output() closed = new EventEmitter<void>();
    @Output() optionSelected = new EventEmitter<{ value: string; description?: string }>();
    @Output() focusedIndexChange = new EventEmitter<number>();

    // If position.width smaller than 250px, hide description
    hideDescription = false;

    isOpen = false;
    position: TextAutocompletePanelPosition = {top: 0, left: 0, width: 150};

    open(position?: Partial<TextAutocompletePanelPosition>): void {
        if (position) this.setPosition(position);
        if (this.isOpen) return;
        this.isOpen = true;
        this.opened.emit();
    }

    close(): void {
        if (!this.isOpen) return;
        this.isOpen = false;
        this.closed.emit();
    }

    setPosition(position: Partial<TextAutocompletePanelPosition>): void {
        this.position = {
            top: Number.isFinite(position.top) ? (position.top as number) : this.position.top,
            left: Number.isFinite(position.left) ? (position.left as number) : this.position.left,
            width: Number.isFinite(position.width) ? (position.width as number) : this.position.width
        };

        this.hideDescription = this.dropdownCriticalWidth && this.position.width < this.dropdownCriticalWidth;
    }

    scrollItemIntoView(index: number): void {
        const container = this.panelEl?.nativeElement;
        const item = this.autocompleteItemButtons?.get(index)?.nativeElement;
        if (!container || !item) return;

        const itemOffsetTop = item.offsetTop;
        const itemOffsetBottom = itemOffsetTop + item.offsetHeight;
        const currentScrollTop = container.scrollTop;
        const visibleHeight = container.clientHeight;
        const visibleBottom = currentScrollTop + visibleHeight;

        if (itemOffsetTop < currentScrollTop) {
            container.scrollTop = itemOffsetTop;
        } else if (itemOffsetBottom > visibleBottom) {
            container.scrollTop = itemOffsetBottom - visibleHeight;
        }
    }

    onOptionSelected(item: { value: string; description?: string }): void {
        this.optionSelected.emit(item);
    }

    onFocusedIndexChange(index: number): void {
        this.focusedIndexChange.emit(index);
    }

    // This prevents blur event from being triggered when clicking within the panel (including the scrollbar).
    onPanelMouseDown(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();
    }
}
